import { z } from "zod";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { ClinicalSpecialtySelectionSchema } from "./referral-specialties.js";
const boundedCode = z.string().regex(/^[A-Z][A-Z0-9_]{0,63}$/);
const boundedVersion = z.string().min(1).max(80);
const opaquePartnerReference = z.string().regex(/^[A-Za-z0-9._:-]{1,160}$/);
export const ReferralChannelSchema = z.enum(["ereferral", "fax"]);
export const ReferralDeliveryModeSchema = z.enum(["ocean_ereferral", "efax", "prepare_only"]);
export const ReferralDestinationModeSchema = z.enum([
    "clinician_selected",
    "automatic",
    "staff_selected",
]);
export const ReferralConnectorProviderSchema = z.enum([
    "fake_ereferral",
    "fake_fax",
    "ocean_ereferral",
    "efax",
    "srfax",
    "internal",
    "signalwire",
    "health_gorilla",
]);
export const ReferralDataClassificationSchema = z.enum(["synthetic", "phi"]);
export const ReferralCallbackEventKindSchema = z.enum([
    "delivery_acknowledged",
    "delivery_failed",
    "recipient_accepted",
    "recipient_rejected",
    "appointment_booked",
    "consult_report_received",
]);
export const ReferralGeoPointSchema = z
    .object({
    latitude: z.number().finite().min(-90).max(90),
    longitude: z.number().finite().min(-180).max(180),
})
    .strict();
export const ReferralWaitTimePreferenceSchema = z
    .object({
    mode: z.enum(["none", "shortest", "balanced"]),
    maximumWaitDays: z.number().int().nonnegative().max(3650).optional(),
    distanceWeight: z.number().finite().min(0).max(1),
    waitTimeWeight: z.number().finite().min(0).max(1),
})
    .strict()
    .superRefine((value, context) => {
    const total = value.distanceWeight + value.waitTimeWeight;
    if (Math.abs(total - 1) > 0.000_001) {
        context.addIssue({
            code: "custom",
            path: ["distanceWeight"],
            message: "Referral routing weights must sum to one",
        });
    }
    if (value.mode === "none" && value.waitTimeWeight !== 0) {
        context.addIssue({
            code: "custom",
            path: ["waitTimeWeight"],
            message: "Routing without a wait-time preference cannot weight wait time",
        });
    }
});
export const ClinicReferralPolicyConfigurationSchema = z
    .object({
    deliveryMode: ReferralDeliveryModeSchema,
    destinationMode: ReferralDestinationModeSchema,
    clinicOrigin: z
        .object({
        locationId: IdSchema,
        displayName: z.string().min(1).max(240),
        point: ReferralGeoPointSchema,
    })
        .strict(),
    maximumTravelRadiusKm: z.number().finite().positive().max(2_000),
    requiredServiceCodes: z.array(z.string().min(1).max(80)).max(100),
    requiredSpecialtyCodes: z.array(z.string().min(1).max(80)).min(1).max(100),
    /** Clinical choices for new referrals; absent on historical policies. */
    clinicalSpecialties: ClinicalSpecialtySelectionSchema.optional(),
    acceptingNewPatientsRequired: z.boolean(),
    waitTimePreference: ReferralWaitTimePreferenceSchema,
    requiredLanguages: z.array(z.string().min(2).max(80)).max(30),
    requiredAccessibilityFeatures: z.array(z.string().min(1).max(120)).max(30),
    maximumDirectoryDataAgeHours: z
        .number()
        .int()
        .positive()
        .max(24 * 365),
    materialTieThreshold: z.number().finite().nonnegative().max(1),
    tieBehavior: z.literal("require_human_selection"),
    uncertaintyBehavior: z.literal("require_human_selection"),
    externalTransmissionEnabled: z.boolean(),
})
    .strict()
    .superRefine((configuration, context) => {
    if (configuration.externalTransmissionEnabled &&
        configuration.destinationMode === "clinician_selected") {
        context.addIssue({
            code: "custom",
            path: ["destinationMode"],
            message: "Automatic fax sending requires automatic or staff-selected routing",
        });
    }
    if ((configuration.externalTransmissionEnabled ||
        configuration.destinationMode === "staff_selected") &&
        configuration.deliveryMode !== "efax") {
        context.addIssue({
            code: "custom",
            path: ["deliveryMode"],
            message: "Electronic sending and staff-selected destinations require eFax delivery",
        });
    }
    const uniqueFields = [
        ["requiredServiceCodes", configuration.requiredServiceCodes],
        ["requiredSpecialtyCodes", configuration.requiredSpecialtyCodes],
        ["requiredLanguages", configuration.requiredLanguages],
        ["requiredAccessibilityFeatures", configuration.requiredAccessibilityFeatures],
    ];
    for (const [path, values] of uniqueFields) {
        if (new Set(values).size !== values.length) {
            context.addIssue({ code: "custom", path: [path], message: `${path} must be unique` });
        }
    }
});
export const ClinicReferralPolicySchema = z
    .object({
    policyId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    version: z.number().int().positive(),
    status: z.enum(["active", "superseded"]),
    ...ClinicReferralPolicyConfigurationSchema.shape,
    effectiveAt: IsoDateTimeSchema,
    updatedBy: IdSchema,
    updatedAt: IsoDateTimeSchema,
    auditEvidence: z
        .object({
        evidenceId: IdSchema,
        contentHash: Sha256Schema,
    })
        .strict(),
})
    .strict()
    .superRefine((policy, context) => {
    if (policy.externalTransmissionEnabled && policy.destinationMode === "clinician_selected") {
        context.addIssue({
            code: "custom",
            path: ["destinationMode"],
            message: "Automatic fax sending requires automatic or staff-selected routing",
        });
    }
    if ((policy.externalTransmissionEnabled || policy.destinationMode === "staff_selected") &&
        policy.deliveryMode !== "efax") {
        context.addIssue({
            code: "custom",
            path: ["deliveryMode"],
            message: "Electronic sending and staff-selected destinations require eFax delivery",
        });
    }
    for (const [path, values] of [
        ["requiredServiceCodes", policy.requiredServiceCodes],
        ["requiredSpecialtyCodes", policy.requiredSpecialtyCodes],
        ["requiredLanguages", policy.requiredLanguages],
        ["requiredAccessibilityFeatures", policy.requiredAccessibilityFeatures],
    ]) {
        if (new Set(values).size !== values.length) {
            context.addIssue({ code: "custom", path: [path], message: `${path} must be unique` });
        }
    }
});
export const SaveClinicReferralPolicyRequestSchema = z
    .object({
    expectedVersion: z.number().int().nonnegative(),
    configuration: ClinicReferralPolicyConfigurationSchema,
    idempotencyKey: IdSchema,
})
    .strict();
export const ReferralRoutingConstraintsSchema = z
    .object({
    clinicOrigin: ClinicReferralPolicyConfigurationSchema.shape.clinicOrigin,
    maximumTravelRadiusKm: ClinicReferralPolicyConfigurationSchema.shape.maximumTravelRadiusKm,
    requiredServiceCodes: ClinicReferralPolicyConfigurationSchema.shape.requiredServiceCodes,
    requiredSpecialtyCodes: ClinicReferralPolicyConfigurationSchema.shape.requiredSpecialtyCodes,
    clinicalSpecialties: ClinicReferralPolicyConfigurationSchema.shape.clinicalSpecialties,
    acceptingNewPatientsRequired: ClinicReferralPolicyConfigurationSchema.shape.acceptingNewPatientsRequired,
    waitTimePreference: ClinicReferralPolicyConfigurationSchema.shape.waitTimePreference,
    requiredLanguages: ClinicReferralPolicyConfigurationSchema.shape.requiredLanguages,
    requiredAccessibilityFeatures: ClinicReferralPolicyConfigurationSchema.shape.requiredAccessibilityFeatures,
    maximumDirectoryDataAgeHours: ClinicReferralPolicyConfigurationSchema.shape.maximumDirectoryDataAgeHours,
    materialTieThreshold: ClinicReferralPolicyConfigurationSchema.shape.materialTieThreshold,
    tieBehavior: ClinicReferralPolicyConfigurationSchema.shape.tieBehavior,
    uncertaintyBehavior: ClinicReferralPolicyConfigurationSchema.shape.uncertaintyBehavior,
})
    .strict();
export const ReferralRoutingAuthorizationSchema = z
    .object({
    policyId: IdSchema,
    policyVersion: z.number().int().positive(),
    policyContentHash: Sha256Schema,
    constraints: ReferralRoutingConstraintsSchema,
    contentHash: Sha256Schema,
})
    .strict();
export const ReferralDestinationRequirementsSchema = z
    .object({
    requirementsVersion: z.number().int().positive(),
    specialtyCodes: z.array(z.string().min(1).max(80)).min(1),
    requiredClinicalFields: z.array(z.string().min(1).max(80)),
    attachmentRules: z.array(z
        .object({
        documentType: z.string().min(1).max(80),
        required: z.boolean(),
        purpose: z.string().min(1).max(500),
        minimumNecessaryReason: z.string().min(1).max(500),
        maximumAgeDays: z.number().int().positive().optional(),
        allowedSensitivityLabels: z.array(z.string().min(1).max(80)).optional(),
    })
        .strict()),
    acceptedContentTypes: z.array(z.string().min(1).max(160)).min(1),
    maximumAttachmentCount: z.number().int().positive().max(50),
    maximumTotalBytes: z.number().int().positive(),
    patientConsentRequired: z.boolean(),
})
    .strict()
    .superRefine((requirements, context) => {
    if (new Set(requirements.specialtyCodes).size !== requirements.specialtyCodes.length) {
        context.addIssue({
            code: "custom",
            path: ["specialtyCodes"],
            message: "Referral destination specialty codes must be unique",
        });
    }
    const documentTypes = requirements.attachmentRules.map((rule) => rule.documentType);
    if (new Set(documentTypes).size !== documentTypes.length) {
        context.addIssue({
            code: "custom",
            path: ["attachmentRules"],
            message: "Referral attachment requirements must have unique document types",
        });
    }
});
export const ReferralDirectoryEndpointSchema = z
    .object({
    endpointId: IdSchema,
    channel: ReferralChannelSchema,
    provider: ReferralConnectorProviderSchema,
    routingAddress: z.string().min(1).max(240),
    active: z.boolean(),
    lastTestedAt: IsoDateTimeSchema,
})
    .strict();
export const ReferralDirectoryVerificationSchema = z
    .object({
    status: z.enum(["verified", "failed", "stale"]),
    method: z.enum(["owner_attestation", "test_transaction", "trusted_registry"]),
    verifiedAt: IsoDateTimeSchema,
    validUntil: IsoDateTimeSchema,
    verifiedBy: IdSchema,
    evidenceReferenceId: IdSchema,
})
    .strict();
export const ReferralDirectoryRecordSchema = z
    .object({
    directoryRecordId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    destinationId: IdSchema,
    directoryVersion: z.number().int().positive(),
    displayName: z.string().min(1).max(240),
    specialtyCodes: z.array(z.string().min(1).max(80)).min(1),
    serviceCodes: z.array(z.string().min(1).max(80)).optional(),
    location: ReferralGeoPointSchema.optional(),
    acceptingNewPatients: z.boolean().optional(),
    languages: z.array(z.string().min(2).max(80)).optional(),
    accessibilityFeatures: z.array(z.string().min(1).max(120)).optional(),
    directoryObservedAt: IsoDateTimeSchema.optional(),
    waitTime: z
        .object({
        days: z.number().int().nonnegative().max(3650),
        source: z.string().min(1).max(240),
        observedAt: IsoDateTimeSchema,
    })
        .strict()
        .optional(),
    oceanListingRef: z.string().min(1).max(160).optional(),
    oceanOfferingRef: z.string().min(1).max(160).optional(),
    supersedesDirectoryRecordId: IdSchema.optional(),
    supersededByDirectoryRecordId: IdSchema.optional(),
    status: z.enum(["active", "inactive", "suspended"]),
    environment: z.enum(["synthetic", "production"]),
    ownerId: IdSchema,
    backupOwnerId: IdSchema,
    verificationCadenceDays: z.number().int().positive().max(365),
    nextVerificationDueAt: IsoDateTimeSchema,
    verification: ReferralDirectoryVerificationSchema,
    requirements: ReferralDestinationRequirementsSchema,
    endpoints: z.array(ReferralDirectoryEndpointSchema).min(1),
    updatedAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((record, context) => {
    if (record.ownerId === record.backupOwnerId) {
        context.addIssue({
            code: "custom",
            path: ["backupOwnerId"],
            message: "Referral directory ownership requires a distinct backup owner",
        });
    }
    if (new Set(record.specialtyCodes).size !== record.specialtyCodes.length) {
        context.addIssue({
            code: "custom",
            path: ["specialtyCodes"],
            message: "Referral directory specialty codes must be unique",
        });
    }
    const endpointIds = record.endpoints.map((endpoint) => endpoint.endpointId);
    if (new Set(endpointIds).size !== endpointIds.length) {
        context.addIssue({
            code: "custom",
            path: ["endpoints"],
            message: "Referral directory endpoint identifiers must be unique",
        });
    }
    if (Date.parse(record.verification.verifiedAt) >= Date.parse(record.verification.validUntil)) {
        context.addIssue({
            code: "custom",
            path: ["verification", "validUntil"],
            message: "Referral verification must expire after it was performed",
        });
    }
    if (Date.parse(record.nextVerificationDueAt) > Date.parse(record.verification.validUntil)) {
        context.addIssue({
            code: "custom",
            path: ["nextVerificationDueAt"],
            message: "Referral re-verification cannot be scheduled after verification expiry",
        });
    }
    for (const [path, values] of [
        ["serviceCodes", record.serviceCodes ?? []],
        ["languages", record.languages ?? []],
        ["accessibilityFeatures", record.accessibilityFeatures ?? []],
    ]) {
        if (new Set(values).size !== values.length) {
            context.addIssue({ code: "custom", path: [path], message: `${path} must be unique` });
        }
    }
});
export const ReferralCandidateExclusionReasonSchema = z.enum([
    "inactive",
    "superseded",
    "not_accepting_patients",
    "specialty_mismatch",
    "service_mismatch",
    "outside_travel_radius",
    "directory_evidence_missing",
    "directory_evidence_stale",
    "wait_time_evidence_missing",
    "wait_time_evidence_stale",
    "wait_time_exceeds_limit",
    "language_preference_unsatisfied",
    "accessibility_preference_unsatisfied",
    "verification_invalid",
    "endpoint_unavailable",
]);
export const ReferralCandidateEvaluationSchema = z
    .object({
    directoryRecordId: IdSchema,
    destinationId: IdSchema,
    directoryVersion: z.number().int().positive(),
    displayName: z.string().min(1).max(240),
    eligible: z.boolean(),
    exclusionReasons: z.array(ReferralCandidateExclusionReasonSchema),
    distanceKm: z.number().finite().nonnegative().optional(),
    distanceMethod: z.literal("haversine_v1").optional(),
    waitTime: z
        .object({
        days: z.number().int().nonnegative(),
        source: z.string().min(1).max(240),
        observedAt: IsoDateTimeSchema,
    })
        .strict()
        .optional(),
    rankingInputs: z
        .object({
        normalizedDistance: z.number().finite().min(0).max(1).optional(),
        normalizedWait: z.number().finite().min(0).max(1).optional(),
        score: z.number().finite().min(0).max(1).optional(),
    })
        .strict(),
    rank: z.number().int().positive().optional(),
})
    .strict()
    .superRefine((candidate, context) => {
    if (candidate.eligible === candidate.exclusionReasons.length > 0) {
        context.addIssue({
            code: "custom",
            path: ["exclusionReasons"],
            message: "Eligible routing candidates cannot carry exclusion reasons",
        });
    }
});
export const ReferralRoutingResolutionSchema = z
    .object({
    resolutionId: IdSchema,
    policyId: IdSchema,
    policyVersion: z.number().int().positive(),
    routingAuthorizationHash: Sha256Schema,
    /** Exact reviewed specialty for new resolutions; absent on historical receipts. */
    requestedSpecialtyCode: z.string().min(1).max(80).optional(),
    directorySnapshotHash: Sha256Schema,
    directoryObservedAt: IsoDateTimeSchema,
    candidates: z.array(ReferralCandidateEvaluationSchema).min(1).max(500),
    outcome: z.enum(["resolved", "human_selection_required"]),
    selectedDestinationId: IdSchema.optional(),
    selectedDirectoryRecordId: IdSchema.optional(),
    reason: z.enum([
        "highest_ranked_eligible",
        "no_eligible_destination",
        "material_tie",
        "required_evidence_missing",
        "required_evidence_stale",
        "patient_preference_unsatisfied",
    ]),
    resolvedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((resolution, context) => {
    const hasSelection = resolution.selectedDestinationId !== undefined &&
        resolution.selectedDirectoryRecordId !== undefined;
    if ((resolution.outcome === "resolved") !== hasSelection) {
        context.addIssue({
            code: "custom",
            path: ["selectedDestinationId"],
            message: "Only resolved routing outcomes may carry a selected destination",
        });
    }
});
export const ReferralDestinationBindingSchema = z
    .object({
    directoryRecordId: IdSchema,
    destinationId: IdSchema,
    directoryVersion: z.number().int().positive(),
    requirementsVersion: z.number().int().positive(),
    endpointId: IdSchema,
    channel: ReferralChannelSchema,
    provider: ReferralConnectorProviderSchema,
    routingAddress: z.string().min(1).max(240),
    verifiedAt: IsoDateTimeSchema,
    verificationValidUntil: IsoDateTimeSchema,
})
    .strict();
/**
 * Where a manual-print referral is addressed. The clinic prints the sealed
 * letter and sends it itself, so this is free text the physician typed (a
 * named specialist, a clinic, a fax number) rather than a verified directory
 * record. It carries no routing address and never authorizes a transmission.
 */
export const ReferralManualDestinationSchema = z
    .object({
    displayName: z.string().trim().min(1).max(240),
    contactDetails: z.string().trim().min(1).max(500).optional(),
})
    .strict();
/**
 * Reserved destination form-answer keys that carry a manual-print letter's
 * addressee. The referral action payload shape is part of the approved model
 * release (its JSON schema is what the OpenAI action-draft provider is asked to
 * fill), so the addressee travels in the existing `formAnswers` map instead of a
 * new payload field; the database projects it into the clinical package's
 * `manualDestination`.
 */
export const MANUAL_REFERRAL_ADDRESSEE_KEY = "addressedTo";
export const MANUAL_REFERRAL_CONTACT_KEY = "addresseeContact";
export function isManualReferralFormAnswerKey(key) {
    return key === MANUAL_REFERRAL_ADDRESSEE_KEY || key === MANUAL_REFERRAL_CONTACT_KEY;
}
/** The addressee recorded in a referral's form answers, if any. */
export function manualDestinationFromFormAnswers(formAnswers) {
    const displayName = formAnswers?.[MANUAL_REFERRAL_ADDRESSEE_KEY]?.trim();
    if (displayName === undefined || displayName.length === 0)
        return undefined;
    const contactDetails = formAnswers?.[MANUAL_REFERRAL_CONTACT_KEY]?.trim();
    return ReferralManualDestinationSchema.parse({
        displayName,
        ...(contactDetails === undefined || contactDetails.length === 0 ? {} : { contactDetails }),
    });
}
/** Form answers with the addressee written into the reserved keys. */
export function formAnswersWithManualDestination(formAnswers, manualDestination) {
    return {
        ...formAnswers,
        [MANUAL_REFERRAL_ADDRESSEE_KEY]: manualDestination.displayName,
        ...(manualDestination.contactDetails === undefined
            ? {}
            : { [MANUAL_REFERRAL_CONTACT_KEY]: manualDestination.contactDetails }),
    };
}
/**
 * Manual-print referrals: the clinic policy leaves destination selection to
 * the clinician (`clinician_selected`), delivery only prepares a sealed PDF
 * (`prepare_only`), and no directory destination is bound. The clinic prints
 * and sends the letter; nothing is transmitted electronically, and no
 * electronic destination is required to draft, approve, or seal it.
 */
export function isManualPrintReferral(payload) {
    return (payload.destinationMode === "clinician_selected" &&
        payload.deliveryMode === "prepare_only" &&
        payload.destinationId === undefined);
}
/** The physician approves clinical content and the exact policy for later staff recipient selection. */
export function isStaffSelectedFaxReferral(payload) {
    return (payload.deliveryMode === "efax" &&
        payload.destinationMode === "staff_selected" &&
        payload.destinationId === undefined);
}
/**
 * Referrals the Agent API prepares locally right after acceptance: automatic
 * routing (destination resolved from directory evidence) and manual print
 * (no destination). Both produce a sealed PDF and never a partner dispatch.
 */
export function isLocallyPreparedReferral(payload) {
    return (payload.destinationMode === "automatic" ||
        isManualPrintReferral(payload) ||
        isStaffSelectedFaxReferral(payload));
}
export const ReferralAttachmentBindingSchema = z
    .object({
    attachmentId: IdSchema,
    documentId: IdSchema,
    documentType: z.string().min(1).max(80),
    objectVersion: z.string().min(1).max(160),
    contentHash: Sha256Schema,
    contentType: z.string().min(1).max(160),
    byteLength: z.number().int().positive(),
    selectionReason: z.string().min(1).max(500).optional(),
    minimumNecessaryReason: z.string().min(1).max(500).optional(),
    sensitivityLabels: z.array(z.string().min(1).max(80)).optional(),
})
    .strict();
export const ReferralClinicalPackageSchema = z
    .object({
    clinicalPackageId: IdSchema,
    version: z.number().int().positive(),
    tenantId: IdSchema,
    siteId: IdSchema,
    patient: z
        .object({
        patientId: IdSchema,
        displayName: z.string().min(1).max(240),
        dateOfBirth: z.string().date().optional(),
        // The provincial/federal coverage member ID (clinical.coverages.member_id,
        // max 120) — never a separately recorded number.
        healthNumber: z.string().min(1).max(120).optional(),
        healthCardVersion: z.string().min(1).max(20).optional(),
        administrativeSex: z.string().min(1).max(80).optional(),
        address: z.string().min(1).max(2_000).optional(),
        phone: z.string().min(1).max(240).optional(),
        email: z.string().min(1).max(320).optional(),
        contact: z.string().min(1).max(240).optional(),
    })
        .strict(),
    referringProvider: z
        .object({
        providerId: IdSchema,
        displayName: z.string().min(1).max(240),
        licenseNumber: z.string().min(1).max(80).optional(),
        ohipBillingNumber: z.string().min(1).max(80).optional(),
        groupNumber: z.string().min(1).max(80).optional(),
        clinicName: z.string().min(1).max(240),
        contact: z.string().min(1).max(240).optional(),
    })
        .strict(),
    encounterId: IdSchema,
    referralId: IdSchema,
    deliveryMode: ReferralDeliveryModeSchema,
    destinationMode: ReferralDestinationModeSchema,
    requestedSpecialtyCode: z.string().min(1).max(80),
    requestedServiceCodes: z.array(z.string().min(1).max(80)).max(100),
    urgency: z.enum(["routine", "urgent", "emergent"]),
    referralQuestion: z.string().min(1).max(4_000),
    clinicalRationale: z.string().min(1).max(8_000),
    relevantHistory: z.array(z.string().min(1).max(2_000)).max(100),
    medications: z.array(z.string().min(1).max(500)).max(200),
    allergies: z.array(z.string().min(1).max(500)).max(200),
    problems: z.array(z.string().min(1).max(500)).max(200),
    formAnswers: z.record(z.string().min(1).max(120), z.string().max(4_000)),
    /** Absent only for a manual-print referral, which binds no directory destination. */
    destination: ReferralDestinationBindingSchema.optional(),
    /** Free-text addressee of a manual-print letter; the clinic sends it. */
    manualDestination: ReferralManualDestinationSchema.optional(),
    attachments: z.array(ReferralAttachmentBindingSchema).max(50),
    consentEvidence: z
        .object({
        required: z.boolean(),
        evidenceId: IdSchema.optional(),
        recordedAt: IsoDateTimeSchema.optional(),
    })
        .strict(),
    approvalBinding: z
        .object({
        approvalReceiptId: IdSchema,
        actionId: IdSchema,
        actionVersion: z.number().int().positive(),
        exactActionHash: Sha256Schema,
        attachmentManifestHash: Sha256Schema,
        routingAuthorizationHash: Sha256Schema.optional(),
    })
        .strict(),
    routingAuthorization: ReferralRoutingAuthorizationSchema.optional(),
    routingResolution: ReferralRoutingResolutionSchema.optional(),
    idempotencyKey: IdempotencyKeySchema,
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((pkg, context) => {
    if (pkg.consentEvidence.required && pkg.consentEvidence.evidenceId === undefined) {
        context.addIssue({
            code: "custom",
            path: ["consentEvidence", "evidenceId"],
            message: "Required referral consent evidence is missing",
        });
    }
    if (pkg.destinationMode === "automatic" &&
        (pkg.routingAuthorization === undefined ||
            pkg.routingResolution === undefined ||
            pkg.approvalBinding.routingAuthorizationHash !== pkg.routingAuthorization.contentHash)) {
        context.addIssue({
            code: "custom",
            path: ["routingResolution"],
            message: "An automatic canonical referral package requires its approved routing authorization and resolution",
        });
    }
    if (pkg.routingResolution !== undefined &&
        (pkg.routingResolution.outcome !== "resolved" ||
            pkg.destination?.destinationId !== pkg.routingResolution.selectedDestinationId)) {
        context.addIssue({
            code: "custom",
            path: ["destination"],
            message: "Canonical referral destination must match deterministic routing evidence",
        });
    }
    const manualPrint = isManualPrintReferral({
        deliveryMode: pkg.deliveryMode,
        destinationMode: pkg.destinationMode,
        destinationId: pkg.destination?.destinationId,
    });
    const staffSelected = isStaffSelectedFaxReferral({
        deliveryMode: pkg.deliveryMode,
        destinationMode: pkg.destinationMode,
        destinationId: pkg.destination?.destinationId,
    });
    if (pkg.destination === undefined && !manualPrint && !staffSelected) {
        context.addIssue({
            code: "custom",
            path: ["destination"],
            message: "Only a manual-print or staff-selected eFax package may omit its directory destination",
        });
    }
    if ((manualPrint || staffSelected) && pkg.routingAuthorization === undefined) {
        context.addIssue({
            code: "custom",
            path: ["routingAuthorization"],
            message: "A destination-later referral package binds the exact clinic policy version",
        });
    }
    if (pkg.manualDestination !== undefined && !manualPrint) {
        context.addIssue({
            code: "custom",
            path: ["manualDestination"],
            message: "A manual destination is valid only for a manual-print referral",
        });
    }
});
export const OceanReferralPreparationSchema = z
    .object({
    preparationId: IdSchema,
    clinicalPackageId: IdSchema,
    listingRef: z.string().min(1).max(160),
    offeringRef: z.string().min(1).max(160),
    referralInitialInfos: z.record(z.string().min(1).max(120), z.string().max(8_000)),
    missingRequiredAnswerKeys: z.array(z.string().min(1).max(120)),
    status: z.enum(["ocean_ready", "blocked_missing_information"]),
    externalTransmissionEnabled: z.literal(false),
    preparedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
})
    .strict();
export const ReferralPdfSourceKindSchema = z.enum([
    "referral_letter",
    "health_profile",
    "chart_document",
    "incoming_document",
    "patient_document",
    "clinic_document",
]);
const referralPdfSourceIdentity = {
    ordinal: z.number().int().positive(),
    sourceKind: ReferralPdfSourceKindSchema,
    sourceId: IdSchema,
    sourceVersion: z.string().min(1).max(160),
    sourceHash: Sha256Schema,
    /** Human label for the letter's enclosure list (the approved selection reason). */
    label: z.string().min(1).max(500).optional(),
};
/** A piece whose pages landed in the sealed PDF. */
export const ReferralPdfIncludedSourceSchema = z
    .object({
    ...referralPdfSourceIdentity,
    included: z.literal(true).optional(),
    firstPage: z.number().int().positive(),
    pageCount: z.number().int().positive(),
})
    .strict();
export const ReferralPdfOmissionReasonSchema = z.enum([
    "missing",
    "changed",
    "unreadable",
    "empty",
    "hash_mismatch",
]);
/**
 * An approved piece the sealer could not include. It stays in the manifest at
 * its approved ordinal so the sealed package still accounts for every approved
 * attachment: omit it and say so, never fail the referral (REF-3).
 */
export const ReferralPdfOmittedSourceSchema = z
    .object({
    ...referralPdfSourceIdentity,
    included: z.literal(false),
    reason: ReferralPdfOmissionReasonSchema,
})
    .strict();
export const ReferralPdfSourceManifestSchema = z.union([
    ReferralPdfIncludedSourceSchema,
    ReferralPdfOmittedSourceSchema,
]);
export function isReferralPdfSourceIncluded(entry) {
    return entry.included !== false;
}
/**
 * A chart document the physician may enclose behind the referral letter. Every
 * candidate is an exact object version with its hash; the drafter turns a
 * selection into the release-pinned attachment bindings, so approval binds the
 * same bytes the sealer later merges (or omits, and says so).
 */
export const ReferralEnclosureDocumentTypeSchema = z.enum([
    "incoming_document",
    "patient_document",
    "clinic_document",
]);
export const ReferralEnclosureCandidateSchema = z
    .object({
    documentType: ReferralEnclosureDocumentTypeSchema,
    documentId: IdSchema,
    objectVersion: z.string().min(1).max(160),
    contentHash: Sha256Schema,
    contentType: z.string().min(1).max(160),
    byteLength: z.number().int().positive(),
    pageCount: z.number().int().positive().nullable(),
    title: z.string().min(1).max(240),
    category: z.string().min(1).max(40),
    documentDate: z.string().date(),
    encounterId: IdSchema.nullable(),
    scanStatus: z.string().min(1).max(40),
})
    .strict();
export const ReferralEnclosureSelectionSchema = z
    .object({
    documentType: ReferralEnclosureDocumentTypeSchema,
    documentId: IdSchema,
    /** Suggested relevance for clinician review; final approval still binds the exact document. */
    minimumNecessaryReason: z.string().trim().min(1).max(500).optional(),
})
    .strict();
/** Package order behind the letter: labs, then imaging, then consults, then the rest. */
export function referralEnclosureCategoryRank(category) {
    switch (category) {
        case "lab":
            return 1;
        case "imaging":
            return 2;
        case "consult":
            return 3;
        default:
            return 4;
    }
}
export function sortReferralEnclosureCandidates(candidates) {
    return [...candidates].sort((left, right) => referralEnclosureCategoryRank(left.category) -
        referralEnclosureCategoryRank(right.category) ||
        right.documentDate.localeCompare(left.documentDate) ||
        left.documentId.localeCompare(right.documentId));
}
export const REFERRAL_ENCLOSURE_MINIMUM_NECESSARY_REASON = "Selected by the referring physician as supporting evidence for this referral";
/** The exact, approval-bound binding for an enclosure candidate the physician ticked. */
export function referralAttachmentBindingFromEnclosure(candidate) {
    return {
        attachmentId: candidate.documentId,
        documentId: candidate.documentId,
        documentType: candidate.documentType,
        objectVersion: candidate.objectVersion,
        contentHash: candidate.contentHash,
        contentType: candidate.contentType,
        byteLength: candidate.byteLength,
        selectionReason: `${candidate.title} · ${candidate.documentDate}`.slice(0, 500),
        minimumNecessaryReason: REFERRAL_ENCLOSURE_MINIMUM_NECESSARY_REASON,
    };
}
const REFERRAL_PDF_OMISSION_TEXT = {
    missing: "no longer in the chart at sealing",
    changed: "changed after approval",
    unreadable: "could not be read",
    empty: "had no pages",
    hash_mismatch: "did not match the approved version",
};
/** One wording for the letter and every screen: why an approved piece was not enclosed. */
export function describeReferralPdfOmission(reason) {
    return REFERRAL_PDF_OMISSION_TEXT[reason];
}
/** The label a sealed package prints for an approved attachment binding. */
export function referralAttachmentLabel(binding) {
    return binding.selectionReason ?? `${binding.documentType} · version ${binding.objectVersion}`;
}
export const ReferralPreparedArtifactSchema = z
    .object({
    artifactId: IdSchema,
    clinicalPackageId: IdSchema,
    version: z.number().int().positive(),
    successorOfArtifactId: IdSchema.optional(),
    state: z.literal("sealed"),
    bucket: z.string().min(1).max(80),
    objectName: z.string().min(1).max(1_000),
    objectVersion: z.string().min(1).max(240),
    contentType: z.literal("application/pdf"),
    byteLength: z.number().int().positive(),
    contentHash: Sha256Schema,
    pageCount: z.number().int().positive(),
    sources: z.array(ReferralPdfSourceManifestSchema).min(1),
    sealedAt: IsoDateTimeSchema,
    externalTransmissionEnabled: z.literal(false),
})
    .strict();
export const PatientReferralArtifactSummarySchema = z
    .object({
    artifactId: IdSchema,
    version: z.number().int().positive(),
    successorOfArtifactId: IdSchema.optional(),
    referralId: IdSchema,
    encounterId: IdSchema,
    contentHash: Sha256Schema,
    pageCount: z.number().int().positive(),
    sources: z.array(ReferralPdfSourceManifestSchema).min(1),
    sealedAt: IsoDateTimeSchema,
    status: z.literal("package_ready"),
    pdfPreviewPath: z.string().startsWith("/v1/referrals/").max(500),
    externalTransmissionEnabled: z.literal(false),
})
    .strict();
/**
 * A referral the encounter drafted that is still awaiting the physician's
 * approval (REF-8): the bundle to reopen, and the one referral action in it.
 * Approved and accepted referrals are tracked as workflows instead.
 */
export const EncounterReferralDraftSummarySchema = z
    .object({
    bundleId: IdSchema,
    bundleVersion: z.number().int().positive(),
    state: z.enum(["draft", "validated", "pending_approval", "changes_requested"]),
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    summary: z.string().min(1).max(2_000),
    specialtyCode: z.string().min(1).max(80).nullable(),
    deliveryMode: z.enum(["prepare_only", "efax", "ocean_ereferral"]).nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .strict();
/**
 * A referral this encounter approved (EXECDEV-194): its tracker and the summary its draft
 * carried. A plan row's draft is named after the plan item, so the row whose referral is
 * already approved shows it instead of drafting another.
 */
export const EncounterReferralSummarySchema = z
    .object({
    referralId: IdSchema,
    bundleId: IdSchema,
    actionId: IdSchema,
    summary: z.string().min(1).max(2_000),
    acceptedAt: IsoDateTimeSchema,
})
    .strict();
export const ReferralPreparationStatusSchema = z.enum([
    "routing_pending",
    "human_selection_required",
    "preparing",
    "ocean_ready",
    "efax_ready",
    "package_ready",
    "stopped",
    "reroute_required",
]);
// Receipt for the authenticated preparation retry path. Preparation normally
// runs inline in acceptance; when it fails after the acceptance transaction
// committed, the referral strands in 'preparing' and this is the repair
// surface. Local-only by construction: the literal false is part of the wire
// contract, not a default.
export const ReferralPreparationRetryReceiptSchema = z
    .object({
    referralId: IdSchema,
    acceptanceTransactionId: IdSchema,
    status: ReferralPreparationStatusSchema,
    externalTransmissionEnabled: z.literal(false),
})
    .strict();
export const ReferralExecutionPackageSchema = z
    .object({
    packageId: IdSchema,
    packageVersion: z.number().int().positive(),
    packageContentHash: Sha256Schema,
    dataClassification: ReferralDataClassificationSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema,
    referralId: IdSchema,
    transactionId: IdSchema,
    approvalReceiptId: IdSchema,
    bundleId: IdSchema,
    bundleVersion: z.number().int().positive(),
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    exactActionHash: Sha256Schema,
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    deliveryMode: ReferralDeliveryModeSchema.optional(),
    destinationMode: ReferralDestinationModeSchema.optional(),
    policy: z
        .object({
        policyId: IdSchema,
        version: z.number().int().positive(),
        contentHash: Sha256Schema,
    })
        .strict()
        .optional(),
    routingAuthorization: ReferralRoutingAuthorizationSchema.optional(),
    routingResolution: ReferralRoutingResolutionSchema.optional(),
    destination: ReferralDestinationBindingSchema,
    attachments: z.array(ReferralAttachmentBindingSchema).max(50),
    clinicalPackage: ReferralClinicalPackageSchema.optional(),
    preparationStatus: ReferralPreparationStatusSchema.optional(),
    oceanPreparation: OceanReferralPreparationSchema.optional(),
    preparedArtifact: ReferralPreparedArtifactSchema.optional(),
    externalTransmissionEnabled: z.literal(false).optional(),
    renderedPackage: ReferralAttachmentBindingSchema.optional(),
    createdAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((pkg, context) => {
    const attachmentIds = pkg.attachments.map((attachment) => attachment.attachmentId);
    if (new Set(attachmentIds).size !== attachmentIds.length) {
        context.addIssue({
            code: "custom",
            path: ["attachments"],
            message: "Referral package attachment identifiers must be unique",
        });
    }
    if (pkg.renderedPackage !== undefined &&
        attachmentIds.includes(pkg.renderedPackage.attachmentId)) {
        context.addIssue({
            code: "custom",
            path: ["renderedPackage", "attachmentId"],
            message: "Rendered referral package must be distinct from its source attachments",
        });
    }
    const extended = pkg.deliveryMode !== undefined || pkg.destinationMode !== undefined;
    if (!extended && pkg.attachments.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["attachments"],
            message: "Legacy referral packages require at least one source attachment",
        });
    }
    if (extended &&
        (pkg.deliveryMode === undefined ||
            pkg.destinationMode === undefined ||
            pkg.policy === undefined ||
            pkg.clinicalPackage === undefined ||
            pkg.preparationStatus === undefined ||
            pkg.externalTransmissionEnabled !== false)) {
        context.addIssue({
            code: "custom",
            path: ["deliveryMode"],
            message: "Extended referral packages require exact policy, routing, clinical, and disabled-transmission bindings",
        });
    }
    if (pkg.destinationMode === "automatic" &&
        (pkg.routingAuthorization === undefined || pkg.routingResolution === undefined)) {
        context.addIssue({
            code: "custom",
            path: ["routingResolution"],
            message: "Automatic referral execution requires approved routing evidence",
        });
    }
    if (pkg.oceanPreparation !== undefined && pkg.deliveryMode !== "ocean_ereferral") {
        context.addIssue({
            code: "custom",
            path: ["oceanPreparation"],
            message: "Ocean preparation is valid only for Ocean eReferral delivery mode",
        });
    }
});
export const ReferralDispatchReceiptSchema = z
    .object({
    tenantId: IdSchema,
    referralId: IdSchema,
    transactionId: IdSchema,
    actionId: IdSchema,
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    packageId: IdSchema,
    packageContentHash: Sha256Schema,
    destinationId: IdSchema,
    endpointId: IdSchema,
    channel: ReferralChannelSchema,
    provider: ReferralConnectorProviderSchema,
    partnerReferenceId: opaquePartnerReference,
    idempotencyKey: IdempotencyKeySchema,
    outcome: z.enum(["sent", "duplicate"]),
    dispatchedAt: IsoDateTimeSchema,
})
    .strict();
export const ReferralMatchEvidenceSchema = z
    .object({
    strategy: z.enum(["exact_partner_reference", "controlled_multi_key", "manual"]),
    matchedKeys: z
        .array(z.enum([
        "partner_reference",
        "referral_id",
        "patient_id",
        "destination_id",
        "appointment_id",
        "document_id",
    ]))
        .min(1),
    confidence: z.number().min(0).max(1),
    thresholdApplied: z.number().min(0).max(1),
    autoMatched: z.boolean(),
    matchedBy: z.union([IdSchema, z.literal("deterministic_matcher")]),
    matchedAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((evidence, context) => {
    if (new Set(evidence.matchedKeys).size !== evidence.matchedKeys.length) {
        context.addIssue({
            code: "custom",
            path: ["matchedKeys"],
            message: "Referral matching evidence keys must be unique",
        });
    }
    if (evidence.autoMatched && evidence.strategy === "manual") {
        context.addIssue({
            code: "custom",
            path: ["autoMatched"],
            message: "Manual referral matches cannot be marked as automatic",
        });
    }
    if (evidence.autoMatched && evidence.confidence < evidence.thresholdApplied) {
        context.addIssue({
            code: "custom",
            path: ["confidence"],
            message: "Automatic referral matches must meet their configured threshold",
        });
    }
    if (evidence.strategy === "exact_partner_reference" &&
        !evidence.matchedKeys.includes("partner_reference")) {
        context.addIssue({
            code: "custom",
            path: ["matchedKeys"],
            message: "Exact referral matching requires partner-reference evidence",
        });
    }
});
const referralCallbackBaseShape = {
    tenantId: IdSchema,
    callbackId: IdSchema,
    provider: ReferralConnectorProviderSchema,
    channel: ReferralChannelSchema,
    referralId: IdSchema,
    transactionId: IdSchema,
    actionId: IdSchema,
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    partnerReferenceId: opaquePartnerReference,
    packageId: IdSchema,
    packageContentHash: Sha256Schema,
    destinationId: IdSchema,
    occurredAt: IsoDateTimeSchema,
    receivedAt: IsoDateTimeSchema,
};
export const ReferralDeliveryAcknowledgedCallbackSchema = z
    .object({
    ...referralCallbackBaseShape,
    eventKind: z.literal("delivery_acknowledged"),
})
    .strict();
export const ReferralDeliveryFailedCallbackSchema = z
    .object({
    ...referralCallbackBaseShape,
    eventKind: z.literal("delivery_failed"),
    failureCode: boundedCode,
    retryable: z.boolean(),
})
    .strict();
export const ReferralRecipientAcceptedCallbackSchema = z
    .object({
    ...referralCallbackBaseShape,
    eventKind: z.literal("recipient_accepted"),
    acceptanceCode: z.string().min(1).max(80),
})
    .strict();
export const ReferralRecipientRejectedCallbackSchema = z
    .object({
    ...referralCallbackBaseShape,
    eventKind: z.literal("recipient_rejected"),
    rejectionCode: boundedCode,
    retryable: z.boolean(),
})
    .strict();
export const ReferralAppointmentBookedCallbackSchema = z
    .object({
    ...referralCallbackBaseShape,
    eventKind: z.literal("appointment_booked"),
    appointmentReferenceId: opaquePartnerReference,
    appointmentAt: IsoDateTimeSchema.optional(),
    matchEvidence: ReferralMatchEvidenceSchema,
})
    .strict();
export const ReferralConsultReportReceivedCallbackSchema = z
    .object({
    ...referralCallbackBaseShape,
    eventKind: z.literal("consult_report_received"),
    documentId: IdSchema,
    documentVersion: z.string().min(1).max(160),
    documentContentHash: Sha256Schema,
    matchEvidence: ReferralMatchEvidenceSchema,
})
    .strict();
export const ReferralPartnerCallbackSchema = z.discriminatedUnion("eventKind", [
    ReferralDeliveryAcknowledgedCallbackSchema,
    ReferralDeliveryFailedCallbackSchema,
    ReferralRecipientAcceptedCallbackSchema,
    ReferralRecipientRejectedCallbackSchema,
    ReferralAppointmentBookedCallbackSchema,
    ReferralConsultReportReceivedCallbackSchema,
]);
export const ReferralCallbackReceiptSchema = z
    .object({
    tenantId: IdSchema,
    callbackId: IdSchema,
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    eventKind: ReferralCallbackEventKindSchema,
    outcome: z.enum(["applied", "pending_reconciliation", "duplicate", "conflict"]),
    workflowVersion: z.number().int().positive(),
    recordedAt: IsoDateTimeSchema,
})
    .strict();
export const ReferralReconciliationReceiptSchema = z
    .object({
    tenantId: IdSchema,
    referralId: IdSchema.optional(),
    appliedCount: z.number().int().nonnegative(),
    pendingCount: z.number().int().nonnegative(),
    duplicateCount: z.number().int().nonnegative(),
    conflictCount: z.number().int().nonnegative(),
    checkedAt: IsoDateTimeSchema,
})
    .strict();
export const ReferralOverdueScanReceiptSchema = z
    .object({
    tenantId: IdSchema,
    escalatedCount: z.number().int().nonnegative(),
    pendingCount: z.number().int().nonnegative(),
    checkedAt: IsoDateTimeSchema,
})
    .strict();
export const ReferralClosurePolicySchema = z
    .object({
    policyId: z.string().min(1).max(80),
    policyVersion: boundedVersion,
    requiresDelivery: z.boolean(),
    requiresRecipientAcceptance: z.boolean(),
    requiresAppointment: z.boolean(),
    requiresConsultReport: z.boolean(),
    requiresClinicianReview: z.boolean(),
    requiresPatientCommunication: z.boolean(),
    allowsDocumentedAlternativePlan: z.boolean(),
})
    .strict();
export const ReferralEscalationSchema = z
    .object({
    escalationId: IdSchema,
    reasonCode: boundedCode,
    status: z.enum(["open", "resolved"]),
    ownerId: IdSchema,
    backupOwnerId: IdSchema,
    createdAt: IsoDateTimeSchema,
    resolutionCode: boundedCode.optional(),
    resolvedAt: IsoDateTimeSchema.optional(),
})
    .strict();
export const ReferralAlternativePlanSchema = z
    .object({
    planId: IdSchema,
    reasonCode: boundedCode,
    documentedBy: IdSchema,
    documentedAt: IsoDateTimeSchema,
    satisfies: z
        .array(z.enum(["delivery", "recipient_acceptance", "appointment", "consult_report"]))
        .min(1),
})
    .strict();
export const ReferralClinicianReviewSchema = z
    .object({
    reviewId: IdSchema,
    reviewedBy: IdSchema,
    reviewedAt: IsoDateTimeSchema,
    outcome: z.enum(["no_change", "follow_up_required", "alternative_plan"]),
    evidenceReferenceId: IdSchema,
})
    .strict();
export const ReferralPatientCommunicationSchema = z
    .object({
    communicationId: IdSchema,
    channel: z.enum(["portal", "phone", "sms", "email", "letter", "in_person"]),
    communicatedBy: IdSchema,
    communicatedAt: IsoDateTimeSchema,
    acknowledgementRequired: z.boolean(),
    acknowledgedAt: IsoDateTimeSchema.optional(),
})
    .strict();
export const ReferralWorkflowStatusSchema = z.enum([
    "locally_accepted",
    "queued",
    "delivered",
    "destination_accepted",
    "destination_rejected",
    "appointment_scheduled",
    "consult_report_received",
    "consult_report_reviewed",
    "patient_informed",
    "closed",
]);
export const ReferralTrackingStatusSchema = z.union([
    ReferralWorkflowStatusSchema,
    z.enum(["package_ready", "human_selection_required", "stopped", "reroute_required"]),
]);
export const ReferralWorkflowSchema = z
    .object({
    tenantId: IdSchema,
    referralId: IdSchema,
    transactionId: IdSchema,
    approvalReceiptId: IdSchema,
    bundleId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    fulfillmentId: IdSchema,
    expectedEventId: IdSchema,
    package: ReferralExecutionPackageSchema,
    ownerId: IdSchema,
    backupOwnerId: IdSchema,
    closurePolicy: ReferralClosurePolicySchema,
    status: ReferralWorkflowStatusSchema,
    version: z.number().int().positive(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    dispatch: ReferralDispatchReceiptSchema.optional(),
    callbacks: z.array(ReferralPartnerCallbackSchema),
    callbackFingerprints: z.record(IdSchema, Sha256Schema),
    appliedCallbackIds: z.array(IdSchema),
    pendingCallbackIds: z.array(IdSchema),
    conflictCallbackIds: z.array(IdSchema),
    delivery: ReferralDeliveryAcknowledgedCallbackSchema.optional(),
    recipientDecision: z
        .union([ReferralRecipientAcceptedCallbackSchema, ReferralRecipientRejectedCallbackSchema])
        .optional(),
    appointment: ReferralAppointmentBookedCallbackSchema.optional(),
    consultReport: ReferralConsultReportReceivedCallbackSchema.optional(),
    clinicianReview: ReferralClinicianReviewSchema.optional(),
    patientCommunication: ReferralPatientCommunicationSchema.optional(),
    alternativePlan: ReferralAlternativePlanSchema.optional(),
    escalations: z.array(ReferralEscalationSchema),
    closedAt: IsoDateTimeSchema.optional(),
    closureReasonCode: boundedCode.optional(),
})
    .strict();
/**
 * Curated operational projection exposed to clinician-facing referral tracking surfaces.
 * This is deliberately smaller than ReferralWorkflowSchema and contains no free-form clinical
 * content, partner payload, or routing address.
 */
export const ReferralOperationalExceptionSchema = z
    .object({
    exceptionId: IdSchema,
    kind: z.enum(["overdue", "escalated", "connector_failed"]),
    state: z.enum(["open", "resolved"]),
    openedAt: IsoDateTimeSchema,
    resolvedAt: IsoDateTimeSchema.optional(),
    summary: z.string().min(1).max(240),
})
    .strict();
export const ReferralTrackedExpectedEventSchema = z
    .object({
    expectedEventId: IdSchema,
    eventType: z.enum([
        "delivery_receipt",
        "referral_acceptance",
        "appointment_booking",
        "consult_report",
        "patient_acknowledgement",
    ]),
    status: z.enum(["waiting", "open", "completed", "overdue", "escalated", "failed"]),
    expectedBy: IsoDateTimeSchema,
    overdueAt: IsoDateTimeSchema,
    completedAt: IsoDateTimeSchema.optional(),
})
    .strict();
/**
 * The open print-and-send hand-off behind a referral (REF-5, REF-10): the staff
 * request that carries the sealed PDF and who currently holds it, so the
 * Attention row can belong to that person.
 */
export const ReferralHandoffSummarySchema = z
    .object({
    requestId: IdSchema,
    status: z.enum(["open", "in_progress"]),
    version: z.number().int().positive(),
    artifactVersion: z.number().int().positive().optional(),
    assignedTo: IdSchema.optional(),
    assignedToName: z.string().min(1).max(240).optional(),
})
    .strict();
/**
 * A site's default print-and-send hand-off person (REF-10): who a sealed
 * package lands with unless the physician names someone else. A setting of its
 * own, not part of the policy, so changing it never versions the policy or
 * invalidates pending drafts. No assignee means the clinic administrator.
 */
export const ReferralHandoffDefaultSchema = z
    .object({
    siteId: IdSchema,
    /** 0 until the site has ever saved one. */
    version: z.number().int().nonnegative(),
    assignedTo: IdSchema.optional(),
    assignedToName: z.string().min(1).max(240).optional(),
    updatedAt: IsoDateTimeSchema.optional(),
})
    .strict();
export const SaveReferralHandoffDefaultRequestSchema = z
    .object({
    siteId: IdSchema,
    expectedVersion: z.number().int().nonnegative(),
    /** null clears the choice; hand-offs then go to the clinic administrator. */
    assignedTo: IdSchema.nullable(),
})
    .strict();
/**
 * A clinic the staff can send a printed referral to (REF-7). Anyone at the
 * clinic may add or update one. Historical verification metadata does not
 * restrict fax sending; automatic routing retains its clinical service policies.
 */
export const ReferralDirectoryEntrySchema = z
    .object({
    id: IdSchema,
    version: z.number().int().positive(),
    supersedesId: IdSchema.optional(),
    organizationName: z.string().min(1).max(240),
    specialty: z.string().min(1).max(80),
    fax: z.string().min(1).max(40).optional(),
    phone: z.string().min(1).max(40).optional(),
    address: z.record(z.string(), z.unknown()).optional(),
    acceptingNewPatients: z.boolean(),
    notes: z.string().min(1).max(1_000).optional(),
    verified: z.boolean(),
    verifiedAt: IsoDateTimeSchema.optional(),
    createdAt: IsoDateTimeSchema,
    createdBy: IdSchema.optional(),
})
    .strict();
export const ReferralDirectoryEntryInputSchema = z
    .object({
    organizationName: z.string().trim().min(1).max(240),
    specialty: z.string().trim().min(1).max(80),
    fax: z.string().trim().min(1).max(40).optional(),
    phone: z.string().trim().min(1).max(40).optional(),
    address: z.record(z.string(), z.unknown()).optional(),
    acceptingNewPatients: z.boolean().default(true),
    notes: z.string().trim().min(1).max(1_000).optional(),
})
    .strict();
export const SaveReferralDirectoryEntryRequestSchema = z
    .object({
    siteId: IdSchema,
    entry: ReferralDirectoryEntryInputSchema,
    /** Editing: the entry this one replaces. */
    supersedesId: IdSchema.optional(),
})
    .strict();
export const ReferralWorkQueueItemSchema = z
    .object({
    referralId: IdSchema,
    /** Who and which visit the referral belongs to, so a list can lead with the patient. */
    patientId: IdSchema.optional(),
    patientName: z.string().min(1).max(240).optional(),
    encounterId: IdSchema.optional(),
    /** The referring clinician who owns the referral's acceptance obligation, and their backup. */
    ownerId: IdSchema.optional(),
    backupOwnerId: IdSchema.optional(),
    /** The open hand-off, when staff hold the sealed package (REF-10). */
    handoff: ReferralHandoffSummarySchema.optional(),
    bundleId: IdSchema,
    actionId: IdSchema,
    version: z.number().int().positive(),
    status: ReferralTrackingStatusSchema,
    destinationDisplayName: z.string().min(1).max(160),
    channel: ReferralChannelSchema,
    nextExpectedEvent: ReferralTrackedExpectedEventSchema.optional(),
    exceptions: z.array(ReferralOperationalExceptionSchema),
    latestUpdateAt: IsoDateTimeSchema,
})
    .strict();
export const ReferralWorkQueueViewSchema = z
    .object({ items: z.array(ReferralWorkQueueItemSchema) })
    .strict();
export const ReferralTrackingViewSchema = z
    .object({
    referralId: IdSchema,
    version: z.number().int().positive(),
    status: ReferralTrackingStatusSchema,
    packageBinding: z
        .object({
        bundleId: IdSchema,
        actionId: IdSchema,
        actionVersion: z.number().int().positive(),
        exactPayloadHash: Sha256Schema,
        exactPreviewHash: Sha256Schema,
        destinationId: IdSchema.optional(),
        attachmentIds: z.array(IdSchema),
    })
        .strict(),
    destination: z
        .object({
        destinationId: IdSchema,
        displayName: z.string().min(1).max(160),
        channel: ReferralChannelSchema,
        verificationStatus: z.literal("verified"),
        directoryVersion: z.number().int().positive(),
        verifiedAt: IsoDateTimeSchema,
    })
        .strict()
        .optional(),
    locallyAcceptedAt: IsoDateTimeSchema,
    queuedAt: IsoDateTimeSchema.optional(),
    deliveredAt: IsoDateTimeSchema.optional(),
    destinationDecision: z.enum(["accepted", "rejected"]).optional(),
    destinationDecisionAt: IsoDateTimeSchema.optional(),
    alternativePlanRecordedAt: IsoDateTimeSchema.optional(),
    appointmentAt: IsoDateTimeSchema.optional(),
    appointmentLocation: z.string().max(500).optional(),
    appointmentDate: z.string().date().optional(),
    appointmentNote: z.string().max(2000).optional(),
    linkedDocuments: z
        .array(z
        .object({
        reportId: IdSchema,
        title: z.string().max(240),
        kind: z.enum(["appointment_scheduled", "consult_report_received"]),
    })
        .strict())
        .optional(),
    availableDocuments: z
        .array(z.object({ reportId: IdSchema, title: z.string().max(240) }).strict())
        .optional(),
    consultReportReceivedAt: IsoDateTimeSchema.optional(),
    consultReportReviewedAt: IsoDateTimeSchema.optional(),
    patientInformedAt: IsoDateTimeSchema.optional(),
    closedAt: IsoDateTimeSchema.optional(),
    /** The clinic staff chose for a printed package after approval (REF-7). */
    selectedDestination: z
        .object({
        destinationId: IdSchema,
        version: z.number().int().positive(),
        displayName: z.string().min(1).max(240),
        specialty: z.string().min(1).max(80).optional(),
        fax: z.string().min(1).max(40).optional(),
        phone: z.string().min(1).max(40).optional(),
        verified: z.boolean(),
    })
        .strict()
        .optional(),
    expectedEvents: z.array(ReferralTrackedExpectedEventSchema),
    exceptions: z.array(ReferralOperationalExceptionSchema),
    preparation: z
        .object({
        deliveryMode: ReferralDeliveryModeSchema,
        destinationMode: ReferralDestinationModeSchema,
        policy: z
            .object({
            policyId: IdSchema,
            version: z.number().int().positive(),
            contentHash: Sha256Schema,
        })
            .strict(),
        status: ReferralPreparationStatusSchema,
        exactClinicalPackage: ReferralClinicalPackageSchema.optional(),
        blockers: z.array(z.string().min(1).max(240)),
        destinationResolvesAfterApproval: z.boolean(),
        routingResolution: ReferralRoutingResolutionSchema.optional(),
        oceanPreparation: OceanReferralPreparationSchema.optional(),
        preparedArtifact: ReferralPreparedArtifactSchema.optional(),
        pdfPreviewPath: z.string().startsWith("/v1/referrals/").max(500).optional(),
        controlVersion: z.number().int().positive(),
        externalTransmissionEnabled: z.boolean(),
    })
        .strict()
        .optional(),
    latestUpdateAt: IsoDateTimeSchema,
})
    .strict();
const ReferralTrackingTransitionRequestBaseSchema = z.object({
    expectedVersion: z.number().int().positive(),
    idempotencyKey: IdSchema,
});
/** Why a physician withdraws a referral that never left the clinic. */
export const ReferralWithdrawalReasonCodeSchema = z.enum([
    "no_longer_clinically_indicated",
    "patient_declined",
    "entered_in_error",
]);
/**
 * States a referral can be withdrawn from: approved, but it never reached a
 * clinic, so there is no answer to wait for and no outcome to tell the patient.
 */
export const ReferralWithdrawableStatusSchema = z.enum([
    "locally_accepted",
    "package_ready",
    "human_selection_required",
    "stopped",
    "reroute_required",
]);
export function isReferralWithdrawable(status) {
    return ReferralWithdrawableStatusSchema.safeParse(status).success;
}
export const ReferralTrackingTransitionRequestSchema = z.discriminatedUnion("transition", [
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("appointment_scheduled"),
        appointmentAt: IsoDateTimeSchema,
        location: z.string().trim().min(1).max(500).optional(),
        note: z.string().trim().min(1).max(2000).optional(),
        reportId: IdSchema.optional(),
    }).strict(),
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("consult_report_received"),
        reportId: IdSchema,
    }).strict(),
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("stop_preparation"),
        reasonCode: boundedCode,
    }).strict(),
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("reroute"),
        reasonCode: boundedCode,
    }).strict(),
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("consult_report_reviewed"),
        dispositionCode: z.enum([
            "no_change",
            "follow_up_required",
            "medication_change",
            "diagnostic_workup",
            "urgent_escalation",
        ]),
    }),
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("patient_informed"),
        channel: z.enum(["phone", "secure_message", "in_person"]),
        outcome: z.enum(["reached", "caregiver_reached"]),
    }),
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("alternative_plan_recorded"),
        alternativePlanCode: z.enum([
            "redirected",
            "primary_care_management",
            "emergency_assessment",
            "specialist_advice",
            "patient_declined",
        ]),
    }),
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("escalated"),
        reasonCode: z.enum([
            "overdue",
            "connector_failed",
            "destination_rejected",
            "missing_response",
            "clinical_urgency",
        ]),
    }),
    // Staff pick the clinic a printed package goes to, after approval (REF-7).
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("destination_selected"),
        destinationId: IdSchema,
        destinationVersion: z.number().int().positive(),
    }).strict(),
    // Staff record that the printed package left the clinic (REF-7): manual until
    // an eFax provider sends it.
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("sent"),
        method: z.enum(["fax", "mail", "in_person", "other"]),
    }).strict(),
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("closed"),
        closureReasonCode: z.enum([
            "completed",
            "alternative_plan_completed",
            "patient_declined",
            "no_longer_clinically_indicated",
        ]),
    }),
    // A physician withdraws a referral that never left the clinic: no clinic
    // chosen, stopped, reroute required, printed but never sent, or still
    // preparing. Open work is cancelled with it.
    ReferralTrackingTransitionRequestBaseSchema.extend({
        transition: z.literal("withdrawn"),
        withdrawalReasonCode: ReferralWithdrawalReasonCodeSchema,
    }).strict(),
]);
export const ReferralTrackingTransitionCommandSchema = z.intersection(z.object({
    tenantId: IdSchema,
    siteId: IdSchema,
    actorId: IdSchema,
    referralId: IdSchema,
}), ReferralTrackingTransitionRequestSchema);
//# sourceMappingURL=referral.js.map